import Template from "../../components/Template";
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import usePermission from "../../hooks/usePermission";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx-js-style";
import { useAuth } from '../../context/AuthContext';
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb } from "../../utils/masks";
import logoEmpresa from "../../components/logoEmpresaBase64";
import { statusParaFiltro } from "../../utils/statusPedidoCompra";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
const RelatorioPedidosCompra = () => {
    const { auth } = useAuth();
    const [lista, setLista] = useState([]);
    const [obras, setObras] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [compradores, setCompradores] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [columns, setColumns] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Estado para armazenar os OBJETOS COMPLETOS das linhas selecionadas
    const [selectedRows, setSelectedRows] = useState([]);

    const [filters, setFilters] = useState({
        obra: "",
        fornecedor: "",
        comprador: "",
        status: "",
        dataInicial: "",
        dataFinal: "",
        formaPagamento: "",
        dataVencimentoInicial: "",
        dataVencimentoFinal: ""
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Lógica para selecionar/deselecionar uma única linha
    const handleRowSelect = (row) => {
        setSelectedRows(prev => {
            const isSelected = prev.some(item => item.id === row.id);
            if (isSelected) {
                return prev.filter(item => item.id !== row.id);
            } else {
                return [...prev, row];
            }
        });
    };

    // Lógica para o checkbox "Selecionar Todos" do cabeçalho
    const handleSelectAllOnPage = (e) => {
        if (e.target.checked) {
            setSelectedRows(prev => {
                const newItems = lista.filter(item => !prev.some(sel => sel.id === item.id));
                return [...prev, ...newItems];
            });
        } else {
            const pageIds = lista.map(item => item.id);
            setSelectedRows(prev => prev.filter(item => !pageIds.includes(item.id)));
        }
    };

    // Definição das colunas
    useEffect(() => {
        setColumns([
            {
                id: 'select',
                label: (
                    <input
                        type="checkbox"
                        title="Selecionar todos nesta página"
                        checked={lista.length > 0 && lista.every(item => selectedRows.some(sel => sel.id === item.id))}
                        onChange={handleSelectAllOnPage}
                    />
                ),
                visible: true,
                formatter: (value, row) => (
                    <input
                        type="checkbox"
                        checked={selectedRows.some(sel => sel.id === row.id)}
                        onChange={() => handleRowSelect(row)}
                    />
                ),
            },
            { id: 'id', label: 'ID', visible: false, formatter: (value) => value || "" },
            { id: 'numeroOC', label: 'Número', visible: true, formatter: (value) => value || "" },
            { id: 'fornecedor', label: 'Fornecedor', visible: true, formatter: (value) => value?.nomeFornecedor?.toUpperCase() || value?.razaoSocial?.toUpperCase() || 'N/A' },
            { id: 'obra', label: 'Obra', visible: true, formatter: (value) => value?.nomeProjeto?.toUpperCase() || 'N/A' },
            { id: 'dataEmissao', label: 'Data Emissão', visible: false, formatter: (value) => value ? formatDateWithoutHour(value) : "" },
            { id: 'formaPagamento', label: 'Forma Pagamento', visible: true, formatter: (value) => value || "" },
            { id: 'pixChave', label: 'Chave PIX', visible: true, formatter: (value, row) => row?.formaPagamento?.toUpperCase().includes('PIX') ? (value || '') : '' },
            { id: 'dataPagamento', label: 'Data Pagto', visible: true, formatter: (value) => value ? formatDateWithoutHour(value) : "" },
            // Há observações de 500+ caracteres (texto do boleto): na tela mostra 3 linhas
            // e o resto no title; Excel e PDF continuam com o texto inteiro
            { id: 'pagamentoObservacoes', label: 'Obs. Pagamento', visible: true, resumirNaTela: true, formatter: (value) => value || "" },
            { id: 'valorFinal', label: 'Valor Bruto', visible: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'valorTotalRetencoes', label: 'Impostos Retidos', visible: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'valorLiquidoFornecedor', label: 'Valor Líquido', visible: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'status', label: 'Status', visible: true, formatter: (value) => value?.nome || value || "" },
            { id: 'temNotaFiscal', label: 'Nota Fiscal', visible: true, formatter: (value) => value ? 'Sim' : 'Não' },
        ]);
    }, [lista, selectedRows]);

    // Busca inicial de dados para filtros
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [obrasRes, fornecedoresRes, usersRes, statusRes] = await Promise.all([
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/ativos/todos").catch(() => api.get("/fornecedores/todos")),
                    api.get("/users"),
                    api.get("/pedidos-compra/status")
                ]);
                setObras(obrasRes.data || []);
                setFornecedores(fornecedoresRes.data || []);
                setCompradores(usersRes.data || []);

                setStatusList(statusParaFiltro(statusRes.data.status));
            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
                setMessage("Erro ao carregar dados para filtros.");
                setShowSuccessDialog(true);
            }
        };
        fetchInitialData();
    }, []);

    // Busca de pedidos
    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchPedidosRelatorio = async (page, size, currentFilters) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('pageSize', size);

            if (currentFilters.obra) params.append('obraId', currentFilters.obra);
            if (currentFilters.fornecedor) params.append('fornecedorId', currentFilters.fornecedor);
            if (currentFilters.comprador) params.append('compradorId', currentFilters.comprador);
            if (currentFilters.status) params.append('statusId', currentFilters.status);
            if (currentFilters.dataInicial) params.append('dataInicio', currentFilters.dataInicial);
            if (currentFilters.dataFinal) params.append('dataFim', currentFilters.dataFinal);
            if (currentFilters.formaPagamento) params.append('formaPagamento', currentFilters.formaPagamento);
            if (currentFilters.dataVencimentoInicial) params.append('dataVencimentoInicio', currentFilters.dataVencimentoInicial);
            if (currentFilters.dataVencimentoFinal) params.append('dataVencimentoFim', currentFilters.dataVencimentoFinal);

            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/relatorios/pedidos-compra?${params.toString()}`);

            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;


            setLista(response.data.records || []);
            setTotalRecords(response.data.total || 0);
            setTotalPages(response.data.totalPages || 0);
            setCurrentPage(response.data.page || 1);

        } catch (error) {
            console.error("Erro ao buscar pedidos de compra:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar pedidos de compra.");
            setShowSuccessDialog(true);
            setLista([]);
            setTotalRecords(0);
        } finally {
            setIsLoading(false);
        }
    };

    // Carregar dados ao montar - busca todos por padrão
    useEffect(() => {
        fetchPedidosRelatorio(1, pageSize, filters);
    }, []);

    useEffect(() => {
        if (totalRecords > 0) {
            fetchPedidosRelatorio(currentPage, pageSize, filters);
        }
    }, [currentPage]);

    const handlePageSizeChange = (newPageSize) => {
        const firstRecordOnPage = (currentPage - 1) * pageSize + 1;
        const newCurrentPage = Math.ceil(firstRecordOnPage / newPageSize);

        setPageSize(newPageSize);
        const newTotalPages = Math.ceil(totalRecords / newPageSize);
        const targetPage = Math.min(newCurrentPage, newTotalPages) || 1;
        setCurrentPage(targetPage);
        fetchPedidosRelatorio(targetPage, newPageSize, filters);
    };

    const applyFilters = () => {
        setCurrentPage(1);
        setSelectedRows([]);
        fetchPedidosRelatorio(1, pageSize, filters);
        setMenuOpen(null);
    };

    const clearFilters = () => {
        const emptyFilters = { obra: "", fornecedor: "", comprador: "", status: "", dataInicial: "", dataFinal: "", formaPagamento: "", dataVencimentoInicial: "", dataVencimentoFinal: "" };
        setFilters(emptyFilters);
        setSelectedRows([]);
        setCurrentPage(1);
        fetchPedidosRelatorio(1, pageSize, emptyFilters);
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const toggleColumnVisibility = (id) => {
        if (id === 'select') return;
        setColumns((prev) => prev.map((col) => col.id === id ? { ...col, visible: !col.visible } : col));
    };

    // Exportação
    const exportarDados = async (exportFunction) => {
        const hasSelection = selectedRows.length > 0;

        if (!hasSelection && (!totalRecords || totalRecords === 0)) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        let dataToExport = [];

        if (hasSelection) {
            dataToExport = selectedRows;
            exportFunction(dataToExport);
        } else {
            setMessage('Preparando todos os dados para exportação... Isso pode levar um momento.');
            setShowSuccessDialog(true);
            try {
                const params = new URLSearchParams();
                params.append('exportAll', 'true');
                if (filters.obra) params.append('obraId', filters.obra);
                if (filters.fornecedor) params.append('fornecedorId', filters.fornecedor);
                if (filters.comprador) params.append('compradorId', filters.comprador);
                if (filters.status) params.append('statusId', filters.status);
                if (filters.dataInicial) params.append('dataInicio', filters.dataInicial);
                if (filters.dataFinal) params.append('dataFim', filters.dataFinal);
                if (filters.formaPagamento) params.append('formaPagamento', filters.formaPagamento);
                if (filters.dataVencimentoInicial) params.append('dataVencimentoInicio', filters.dataVencimentoInicial);
                if (filters.dataVencimentoFinal) params.append('dataVencimentoFim', filters.dataVencimentoFinal);

                const response = await api.get(`/relatorios/pedidos-compra?${params.toString()}`);
                dataToExport = response.data.records || [];
                exportFunction(dataToExport);
            } catch (error) {
                console.error("Erro ao exportar dados:", error);
                setMessage("Falha ao preparar dados para exportação.");
                setShowSuccessDialog(true);
            }
        }
    };

    const exportarPDF = (data) => {
        if (!data || data.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        const doc = new jsPDF({ orientation: "landscape" });

        doc.setFontSize(16);
        doc.text("Relatório de Pedidos de Compra", 14, 20);
        doc.setFontSize(10);

        // Informações dos filtros aplicados
        let yPos = 28;
        if (filters.obra) {
            const selectedObra = obras.find(o => o.id === parseInt(filters.obra));
            doc.text(`Obra: ${selectedObra?.nomeProjeto?.toUpperCase() || 'N/A'}`, 14, yPos);
            yPos += 6;
        }
        if (filters.comprador) {
            const selectedComprador = compradores.find(c => c.id === parseInt(filters.comprador));
            doc.text(`Comprador: ${selectedComprador?.nome?.toUpperCase() || 'N/A'}`, 14, yPos);
            yPos += 6;
        }
        if (filters.dataInicial || filters.dataFinal) {
            doc.text(`Período: ${filters.dataInicial || '...'} a ${filters.dataFinal || '...'}`, 14, yPos);
            yPos += 6;
        }
        if (selectedRows.length > 0) {
            doc.text(`Exportando ${selectedRows.length} linha(s) selecionada(s).`, 14, yPos);
            yPos += 6;
        }

        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const head = [visibleColumns.map(c => c.label)];
        const body = data.map(row => visibleColumns.map(col => {
            const value = row[col.id];
            return col.formatter ? col.formatter(value, row) : (value || '');
        }));

        // Totais das colunas monetárias
        const monetaryColumnIds = ['valorFinal', 'valorTotalRetencoes', 'valorLiquidoFornecedor'];
        const totaisPorColuna = monetaryColumnIds.reduce((acc, id) => {
            acc[id] = data.reduce((sum, item) => sum + (Number(item[id]) || 0), 0);
            return acc;
        }, {});

        // Linha de total: preenche cada coluna monetária com seu total formatado
        const totalRow = Array(head[0].length).fill('');
        const firstMonetaryIndex = visibleColumns.findIndex(c => monetaryColumnIds.includes(c.id));
        if (firstMonetaryIndex > 0) {
            totalRow[firstMonetaryIndex - 1] = { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } };
        }
        visibleColumns.forEach((col, idx) => {
            if (monetaryColumnIds.includes(col.id)) {
                totalRow[idx] = {
                    content: formatCurrencyFromDb(totaisPorColuna[col.id]),
                    styles: { fontStyle: 'bold' }
                };
            }
        });
        body.push(totalRow);

        // Largura por id (em mm). A4 landscape útil ≈ 285mm com margens de 6mm.
        const widthById = {
            id: 12,
            numeroOC: 22,
            fornecedor: 34,
            obra: 44,
            dataEmissao: 18,
            formaPagamento: 20,
            pixChave: 24,
            dataPagamento: 18,
            pagamentoObservacoes: 28,
            valorFinal: 22,
            valorTotalRetencoes: 22,
            valorLiquidoFornecedor: 22,
            status: 18,
            temNotaFiscal: 12,
        };
        const columnStyles = visibleColumns.reduce((acc, col, idx) => {
            const w = widthById[col.id];
            const style = { overflow: 'linebreak' };
            if (w) style.cellWidth = w;
            if (monetaryColumnIds.includes(col.id)) style.halign = 'right';
            acc[idx] = style;
            return acc;
        }, {});

        autoTable(doc, {
            startY: yPos + 2,
            head,
            body,
            margin: { left: 6, right: 6 },
            headStyles: { fillColor: [30, 120, 180], fontSize: 8, halign: 'left' },
            bodyStyles: { valign: 'middle' },
            styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
            columnStyles,
            didDrawCell: (data) => {
                if (data.row.index === body.length - 1) {
                    doc.setFont(undefined, 'bold');
                }
            }
        });

        // Rodapé
        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissao = new Date().toLocaleString('pt-BR');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(`Emitido por: ${auth.user.name || auth.user.nome || "Sistema"}`, 14, doc.internal.pageSize.height - 15);
            doc.text(`Data/Hora: ${dataEmissao}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
            if (logoEmpresa) {
                const logoWidth = 40, logoHeight = 12, pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, doc.internal.pageSize.height - 18, logoWidth, logoHeight);
            }
        }

        doc.save("relatorio_pedidos_compra.pdf");
    };

    const exportarExcel = (data) => {
        if (!data || data.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        // --- Estilos ---
        const borderThin = { style: 'thin', color: { rgb: 'B0B0B0' } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

        const titleStyle = {
            font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: borders,
        };
        const filterStyle = {
            font: { italic: true, sz: 11, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: 'DCE6F1' } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '2E75B6' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: borders,
        };
        const cellStyleEven = {
            font: { sz: 10, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: 'F2F7FB' } },
            alignment: { vertical: 'center' },
            border: borders,
        };
        const cellStyleOdd = {
            font: { sz: 10, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: 'FFFFFF' } },
            alignment: { vertical: 'center' },
            border: borders,
        };
        const currencyStyleEven = {
            ...cellStyleEven,
            alignment: { ...cellStyleEven.alignment, horizontal: 'right' },
            numFmt: 'R$ #,##0.00',
        };
        const currencyStyleOdd = {
            ...cellStyleOdd,
            alignment: { ...cellStyleOdd.alignment, horizontal: 'right' },
            numFmt: 'R$ #,##0.00',
        };
        const totalLabelStyle = {
            font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'right', vertical: 'center' },
            border: borders,
        };
        const totalValueStyle = {
            font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'right', vertical: 'center' },
            border: borders,
            numFmt: 'R$ #,##0.00',
        };
        const totalEmptyStyle = {
            fill: { fgColor: { rgb: '1F4E79' } },
            border: borders,
        };

        // --- Montar informações de filtros ---
        const filterLines = [];
        if (filters.obra) {
            const selectedObra = obras.find(o => o.id === parseInt(filters.obra));
            filterLines.push(`Obra: ${selectedObra?.nomeProjeto?.toUpperCase() || 'N/A'}`);
        }
        if (filters.comprador) {
            const selectedComprador = compradores.find(c => c.id === parseInt(filters.comprador));
            filterLines.push(`Comprador: ${selectedComprador?.nome?.toUpperCase() || 'N/A'}`);
        }
        if (selectedRows.length > 0) {
            filterLines.push(`Exportando ${selectedRows.length} linha(s) selecionada(s).`);
        }

        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const colCount = visibleColumns.length;
        const monetaryColumnIds = ['valorFinal', 'valorTotalRetencoes', 'valorLiquidoFornecedor'];
        const monetaryIndexes = visibleColumns
            .map((c, i) => monetaryColumnIds.includes(c.id) ? i : -1)
            .filter(i => i !== -1);
        const firstMonetaryIndex = monetaryIndexes.length > 0 ? monetaryIndexes[0] : -1;

        // --- Montar dados da planilha ---
        const wsData = [];
        const merges = [];

        // Linha do título (row 0)
        const titleRow = Array(colCount).fill('');
        titleRow[0] = 'Relatório de Pedidos de Compra';
        wsData.push(titleRow);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

        // Linhas de filtro
        filterLines.forEach((line) => {
            const filterRow = Array(colCount).fill('');
            filterRow[0] = line;
            wsData.push(filterRow);
            merges.push({ s: { r: wsData.length - 1, c: 0 }, e: { r: wsData.length - 1, c: colCount - 1 } });
        });

        // Linha em branco separadora
        wsData.push(Array(colCount).fill(''));

        // Cabeçalho da tabela
        const headerRowIndex = wsData.length;
        wsData.push(visibleColumns.map(c => c.label));

        // Linhas de dados
        const dataStartRow = wsData.length;
        data.forEach(row => {
            wsData.push(visibleColumns.map(col => {
                const value = row[col.id];
                if (monetaryColumnIds.includes(col.id)) {
                    return typeof value === 'number' ? value : parseFloat(String(value || '0').replace(',', '.')) || 0;
                }
                return col.formatter ? col.formatter(value, row) : (value || '');
            }));
        });
        const dataEndRow = wsData.length - 1;

        // Linha em branco antes do total
        wsData.push(Array(colCount).fill(''));

        // Linha de total (uma linha somando cada coluna monetária)
        const totalRowIndex = wsData.length;
        const totalRow = Array(colCount).fill('');
        if (firstMonetaryIndex > 0) {
            totalRow[firstMonetaryIndex - 1] = 'TOTAL:';
        }
        monetaryIndexes.forEach(idx => {
            const colId = visibleColumns[idx].id;
            totalRow[idx] = data.reduce((sum, item) => sum + (Number(item[colId]) || 0), 0);
        });
        wsData.push(totalRow);

        // --- Criar worksheet ---
        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet['!merges'] = merges;

        // --- Aplicar estilos ---
        // Título
        for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: 0, c });
            if (!worksheet[ref]) worksheet[ref] = { v: '', t: 's' };
            worksheet[ref].s = titleStyle;
        }

        // Filtros
        for (let r = 1; r <= filterLines.length; r++) {
            for (let c = 0; c < colCount; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: '', t: 's' };
                worksheet[ref].s = filterStyle;
            }
        }

        // Cabeçalho da tabela
        for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: headerRowIndex, c });
            if (worksheet[ref]) worksheet[ref].s = headerStyle;
        }

        // Dados (zebrado)
        for (let r = dataStartRow; r <= dataEndRow; r++) {
            const isEven = (r - dataStartRow) % 2 === 0;
            for (let c = 0; c < colCount; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: '', t: 's' };
                if (monetaryIndexes.includes(c)) {
                    worksheet[ref].s = isEven ? currencyStyleEven : currencyStyleOdd;
                    if (worksheet[ref].v !== '') {
                        worksheet[ref].t = 'n';
                    }
                } else {
                    worksheet[ref].s = isEven ? cellStyleEven : cellStyleOdd;
                }
            }
        }

        // Total
        for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: totalRowIndex, c });
            if (!worksheet[ref]) worksheet[ref] = { v: '', t: 's' };
            if (monetaryIndexes.includes(c)) {
                worksheet[ref].s = totalValueStyle;
                worksheet[ref].t = 'n';
            } else if (c === firstMonetaryIndex - 1) {
                worksheet[ref].s = totalLabelStyle;
            } else {
                worksheet[ref].s = totalEmptyStyle;
            }
        }

        // --- Largura das colunas ---
        worksheet['!cols'] = visibleColumns.map((col, i) => {
            let maxLen = col.label.length;
            data.forEach(row => {
                const val = String(row[col.id] || '');
                if (val.length > maxLen) maxLen = val.length;
            });
            return { wch: Math.min(Math.max(maxLen + 4, 12), 40) };
        });

        // --- Altura da linha do título ---
        worksheet['!rows'] = [{ hpt: 30 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Pedidos de Compra");
        XLSX.writeFile(workbook, "Relatorio_Pedidos_Compra.xlsx");
    };

    const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target) && buttonFiltrarRef.current && !buttonFiltrarRef.current.contains(event.target)) {
            setMenuOpen(null);
        }
    };
    useEffect(() => { document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);

    // Paginação
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Pedidos de Compra</div>

                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium">Ver</div>
                        <select className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm" value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium">Resultados</div>
                    </div>

                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-6 h-6" src="/images/icon/columns.png" alt="Colunas" /><div className="text-stone-600 text-sm font-medium">Colunas</div></div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "colunas" && (<div className="absolute top-full right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-50 p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>{columns.map((col) => (<div key={col.id} className="flex items-center gap-2"><input type="checkbox" id={`col-${col.id}`} checked={col.visible} disabled={col.id === 'select'} onChange={() => toggleColumnVisibility(col.id)} /><label htmlFor={`col-${col.id}`} className="text-sm text-gray-700">{typeof col.label === 'string' ? col.label : col.id}</label></div>))}</div>)}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-8 h-8" src="/images/icon/filter.png" alt="Filtrar" /><div className="text-stone-600 text-sm font-medium">Filtrar</div></div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "filtro" && (
                                <div className="absolute top-full right-0 mt-2 w-[400px] bg-white rounded-md shadow-lg z-50 p-4 border" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex flex-col gap-4">
                                        <div className="text-stone-800 text-xl font-medium">Filtrar Pedidos de Compra</div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Obra</label>
                                            <select name="obra" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.obra} onChange={(e) => setFilters({ ...filters, obra: e.target.value })}>
                                                <option value="">Todas as obras</option>
                                                {obras.map((o) => (<option key={o.id} value={o.id}>{o.nomeProjeto?.toUpperCase()}</option>))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Fornecedor</label>
                                            <select name="fornecedor" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.fornecedor} onChange={(e) => setFilters({ ...filters, fornecedor: e.target.value })}>
                                                <option value="">Todos os fornecedores</option>
                                                {fornecedores.map((f) => (<option key={f.id} value={f.id}>{(f.nomeFornecedor || f.razaoSocial)?.toUpperCase()}</option>))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Comprador</label>
                                            <select name="comprador" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.comprador} onChange={(e) => setFilters({ ...filters, comprador: e.target.value })}>
                                                <option value="">Todos os compradores</option>
                                                {compradores.map((c) => (<option key={c.id} value={c.id}>{c.nome?.toUpperCase()}</option>))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Status</label>
                                            <select name="status" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                                                <option value="">Todos os status</option>
                                                {statusList.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Emissão Inicial</label><input type="date" name="dataInicial" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataInicial} onChange={(e) => setFilters({ ...filters, dataInicial: e.target.value })} /></div>
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Emissão Final</label><input type="date" name="dataFinal" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataFinal} onChange={(e) => setFilters({ ...filters, dataFinal: e.target.value })} /></div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium">Forma de Pagamento</label>
                                            <select name="formaPagamento" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.formaPagamento} onChange={(e) => setFilters({ ...filters, formaPagamento: e.target.value })}>
                                                <option value="">Todas as formas</option>
                                                <option value="BOLETO">Boleto</option>
                                                <option value="PIX">PIX</option>
                                                <option value="TRANSFERENCIA">Transferência Bancária</option>
                                                <option value="DINHEIRO">Dinheiro</option>
                                                <option value="CHEQUE">Cheque</option>
                                                <option value="CARTAO">Cartão</option>
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Vencimento Inicial</label><input type="date" name="dataVencimentoInicial" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataVencimentoInicial} onChange={(e) => setFilters({ ...filters, dataVencimentoInicial: e.target.value })} /></div>
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Vencimento Final</label><input type="date" name="dataVencimentoFinal" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataVencimentoFinal} onChange={(e) => setFilters({ ...filters, dataVencimentoFinal: e.target.value })} /></div>
                                        </div>

                                        <div className="flex items-center justify-between gap-4 mt-4">
                                            <button className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2" onClick={clearFilters}><img className="w-4 h-4" src="/images/icon/eraser.png" alt="Limpar" /><div className="text-stone-500 text-sm">Limpar</div></button>
                                            <button className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2" onClick={applyFilters}><img className="w-4 h-4" src="/images/icon/filter-white.png" alt="Filtrar" /><div className="text-white text-sm">Filtrar</div></button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <button type='button' className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2" onClick={() => exportarDados(exportarExcel)}>
                                <img className="w-4 h-4" src="/images/icon/upload.png" alt="Excel" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Excel</div>
                            </button>
                            <button type='button' className="flex px-4 py-2 bg-slate-600 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={() => exportarDados(exportarPDF)}>
                                <img className="w-4 h-4" src="/images/icon/pdf.png" alt="PDF" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal">PDF</div>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center px-1">
                    <div className="text-sm text-gray-600">
                        {selectedRows.length > 0 && `${selectedRows.length} linha(s) selecionada(s).`}
                    </div>
                    {selectedRows.length > 0 && (
                        <button onClick={() => setSelectedRows([])} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">
                            Limpar seleção
                        </button>
                    )}
                </div>

                {isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="text-stone-600 text-sm">Carregando...</div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>{columns.filter(col => col.visible).map((col) => (<th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>))}</tr>
                        </thead>
                        <tbody>
                            {!isLoading && lista.length > 0 ? (
                                lista.map((doc, index) => (
                                    <tr key={doc.id || index} className="hover:bg-gray-50">
                                        {columns.filter(col => col.visible).map(col => {
                                            const conteudo = col.formatter(doc[col.id], doc);
                                            return (
                                                <td key={`${col.id}-${doc.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">
                                                    {col.resumirNaTela && typeof conteudo === 'string' && conteudo
                                                        ? <div className="line-clamp-3 min-w-[12rem] max-w-[20rem]" title={conteudo}>{conteudo}</div>
                                                        : conteudo}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : !isLoading ? (
                                <tr><td colSpan={columns.filter(c => c.visible).length} className="text-center py-10 text-slate-500">Nenhum registro encontrado. Utilize o filtro para refinar a busca.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>

                {totalRecords > 0 && (
                    <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                        <div className="grow shrink basis-0 text-slate-500 text-base font-normal">Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.</div>
                        <div className="justify-start items-start gap-4 flex">
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" alt="Anterior" /></button>
                            {pageNumbers.map((page) => (<button key={page} className={`w-10 h-10 rounded flex justify-center items-center font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`} onClick={() => handlePageChange(page)}>{page}</button>))}
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" alt="Próxima" /></button>
                        </div>
                    </div>
                )}
                <AlertDialog isVisible={showSuccessDialog} message={message} onClose={() => setShowSuccessDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioPedidosCompra;
